import Container from "@/components/common/Container";
import Layout from "@/components/layout/Layout";
import { cmsUrl, formatDate } from "@/utils/helper";
import axios from "axios";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

function BlogPost({ postData }) {
  const [heroImageUrl, setHeroImageUrl] = useState();
  const [heroCategory, setHeroCategory] = useState(null);
  const [recentBlog, setRecentBlog] = useState([]);
  const [recentBlogLatestImage, setRecentBlogLatestImage] = useState([]);
  const [recentBlogCategory, setRecentBlogCategory] = useState([]);
  const [recommendedBlog, setRecommendedBlog] = useState([]);
  const [recommendedBlogImage, setRecommendedBlogImage] = useState([]);
  const [recommendedBlogCategory, setRecommendedBlogCategory] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [imageResponse, categoryResponse] = await window.Promise.all([
          axios.get(`${cmsUrl}/wp-json/wp/v2/media/${postData.featured_media}`),
          axios.get(
            `${cmsUrl}/wp-json/wp/v2/categories/${postData.categories[0]}`
          ),
        ]);
        const imageUrlData = {
          source_url: imageResponse.data.source_url,
          alt_text: imageResponse.data.alt_text,
        };
        setHeroImageUrl(imageUrlData);
        setHeroCategory(categoryResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
    getRecentFourBlog();
  }, [postData.categories, postData.featured_media]);

  useEffect(() => {
    const recommendedBlogData = async () => {
      try {
        const recommendedBlogs = await axios.get(
          `${cmsUrl}/wp-json/wp/v2/posts?categories=${postData.categories[0]}`
        );
        const filteredData = recommendedBlogs.data.filter(
          (blog) => blog.id !== postData.id
        );
        setRecommendedBlog(filteredData.slice(0, 4));
        const combinedRequestsForRecommendedBlog = filteredData.map(
          async (recommendedBlog) => {
            const [
              imageResponseForRecommendedBlog,
              categoryResponseForRecommendedBlog,
            ] = await window.Promise.all([
              axios.get(
                `${cmsUrl}/wp-json/wp/v2/media/${recommendedBlog.featured_media}`
              ),
              axios.get(
                `${cmsUrl}/wp-json/wp/v2/categories/${recommendedBlog.categories[0]}`
              ),
            ]);
            return {
              recommendedBlogImageUrl:
                imageResponseForRecommendedBlog.data.source_url,
              recommendedBlogAlt: imageResponseForRecommendedBlog.data.alt_text,
              recommendedBlogCategoryName:
                categoryResponseForRecommendedBlog.data.name,
            };
          }
        );
        const combinedResponsesForRecommendedBlog = await window.Promise.all(
          combinedRequestsForRecommendedBlog
        );
        // Correct mapping
        const recommendedBlogLatestImage =
          combinedResponsesForRecommendedBlog.map((value) => ({
            sourceUrl: value.recommendedBlogImageUrl,
            alt_text: value.recommendedBlogAlt,
          }));
        const categoriesForRecommendedBlog =
          combinedResponsesForRecommendedBlog.map(
            (item) => item.recommendedBlogCategoryName
          );
        setRecommendedBlogImage(recommendedBlogLatestImage);
        setRecommendedBlogCategory(categoriesForRecommendedBlog);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    recommendedBlogData();
  }, [postData.categories, postData.id]);
  const getRecentFourBlog = async () => {
    try {
      const res = await axios.get(
        `${cmsUrl}/wp-json/wp/v2/posts?per_page=4&page=1`
      );
      const tempBlog = res.data;
      const combinedRequestsForRecentBlog = tempBlog.map(async (blog) => {
        const [imageResponse, categoryResponse] = await window.Promise.all([
          axios.get(`${cmsUrl}/wp-json/wp/v2/media/${blog.featured_media}`),
          axios.get(`${cmsUrl}/wp-json/wp/v2/categories/${blog.categories[0]}`),
        ]);
        return {
          heroImageUrl: imageResponse.data.source_url,
          altText: imageResponse.data.alt_text,
          categoryName: categoryResponse.data.name,
        };
      });
      const combinedResponsesForRecentBlog = await window.Promise.all(
        combinedRequestsForRecentBlog
      );
      const latestImages = combinedResponsesForRecentBlog.map((item) => ({
        sourceUrl: item.heroImageUrl,
        alt_text: item.altText,
      }));
      const categories = combinedResponsesForRecentBlog.map(
        (item) => item.categoryName
      );
      setRecentBlogLatestImage(latestImages);
      setRecentBlogCategory(categories);
      setRecentBlog(tempBlog);
    } catch (error) {
      console.error("Error fetching latest blogs:", error);
    }
  };

  return (
    <Layout title={postData.title.rendered}>
      <Container large className="pt-24 xl:pt-40 pb-14 md:pb-32 bg-[#f6f6f6]">
        <div className="grid md:grid-cols-2 gap-x-16">
          <div>
            <p className="font-medium text-primary-yellow text-base md:text-xl mb-2 md:mb-5">
              {heroCategory ? heroCategory.name : ""}
            </p>
            <h1
              className="font-bold text-primary-black text-4xl leading-normal lg:text-[40px] mb-5 lg:leading-tight"
              dangerouslySetInnerHTML={{ __html: postData.title.rendered }}
            ></h1>
            <p className="text-primary-black text-sm lg:text-base mb-5 lg:mb-10">
              {formatDate(postData.date)}
            </p>
            <div
              className="text-primary-black text-base lg:text-xl mb-9 lg:mb-0 md:w-3/4 lg:w-full -tracking-tight [&>p]:text-left [&>p]:md:text-justify"
              dangerouslySetInnerHTML={{
                __html: postData.excerpt.rendered,
              }}
            ></div>
          </div>
          <div>
            <div className="fs-0 rounded-2xl overflow-hidden w-fit">
              <Image
                src={heroImageUrl?.source_url ?? "/"}
                alt={heroImageUrl?.alt_text ?? ""}
                width={664}
                height={547}
              />
            </div>
          </div>
        </div>
      </Container>
      <Container large>
        <div className="flex flex-col md:flex-row justify-between gap-14 md:gap-20 pb-14 pt-16 md:pt-32 md:pb-40">
          <div className="md:w-8/12">
            <h2 className="font-medium text-xl md:text-3xl mb-5 md:mb-10">
              Introduction
            </h2>
            <div
              dangerouslySetInnerHTML={{ __html: postData.content.rendered }}
              className="[&_p]:!mt-5 [&_p]:!mb-7 [&_p]:!text-base [&_p]:lg:!text-lg [&>figure]:!mt-4 [&>figure]:!mb-4 [&>h2]:text-2xl [&>h2]:font-bold [&_a]:text-primary-yellow [&_pre]:bg-[#eee] [&_pre]:!mb-6 [&_pre]:!mt-4 [&_pre]:!p-6 [&_h3]:font-bold [&_h3]:text-lg [&_ul]:!mb-6"
            />
            <div className="pt-10 flex justify-between items-center">
              <div className="w-6/12 md:w-4/12 flex items-center">
                <div className="fs-0 mr-2 lg:mr-5 rounded-full w-1/3">
                  <Image
                    src="/assets/director.webp"
                    width={80}
                    height={80}
                    alt="director"
                  />
                </div>
                <div>
                  <p className="text-base md:text-xl font-medium lg:pb-1 text-black">
                    Jinesh Shah
                  </p>
                  <p className="text-sm md:text-xl font-normal lg:pb-1 md:pb-0 text-[#9D9B9B]">
                    Director
                  </p>
                </div>
              </div>
              <div className="w-4/12 flex justify-end">
                <Link href="https://www.facebook.com/ultroneous.technologies">
                  <a className="bg-blue-700 rounded-full flex items-end justify-center mr-2 w-7 h-7">
                    <Image
                      src="/assets/svg/facebook.svg"
                      height={23}
                      width={12}
                      alt="facebook"
                    />
                  </a>
                </Link>
                <Link href="https://twitter.com/ultroneousTech">
                  <a className="bg-black w-7 h-7 rounded-full flex items-center justify-center mr-2">
                    <Image
                      src="/assets/svg/twitter.svg"
                      height={23}
                      width={12}
                      alt="twitter"
                    />
                  </a>
                </Link>
                <Link href="https://www.linkedin.com/company/ultroneous">
                  <a className="bg-[#007EBB] w-7 h-7 rounded-full flex items-center justify-center">
                    <Image
                      src="/assets/svg/linkedin.svg"
                      height={16}
                      width={16}
                      alt="linkedin"
                    />
                  </a>
                </Link>
              </div>
            </div>
          </div>
          <div className="md:w-4/12 pt-10 pb-7 md:py-9 px-3 md:px-7 bg-[#f9f9f9] rounded-xl h-fit md:sticky top-20">
            <h2 className="text-2xl font-semibold pb-7 md:pb-10">
              Recent Post
            </h2>
            {recentBlog.map((value, index) => (
              <Link passHref href={`/blog/${value.slug}`} key={value.id}>
                <a
                  className={`flex gap-x-4 ${
                    index === recentBlog.length - 1
                      ? ""
                      : "border-b border-solid border-[#ebebeb] mb-5 pb-5"
                  }`}
                >
                  <div className="fs-0 w-1/3">
                    <Image
                      src={recentBlogLatestImage[index]?.sourceUrl ?? "/"}
                      width={198}
                      height={162}
                      alt={recentBlogLatestImage[index]?.alt_text ?? ""}
                      className="rounded-lg overflow-hidden"
                    />
                  </div>
                  <div className="w-2/3">
                    <p className="text-xs font-medium text-primary-yellow pt-1 pb-2">
                      {recentBlogCategory[index]}
                    </p>
                    <h3 className="text-base text-black font-semibold line-clamp-2">
                      {value?.title.rendered}
                    </h3>
                  </div>
                </a>
              </Link>
            ))}
          </div>
        </div>
        <div className="pb-36 md:pb-40">
          <h2 className="text-2xl md:text-3xl font-medium pb-5 md:pb-10">
            Recommended Blogs
          </h2>
          <div className="flex flex-col md:flex-row gap-10 md:gap-5">
            {recommendedBlog?.map((value, index) => (
              <Link key={value.id} href={`/blog/${value.slug}`} passHref>
                <a className="!ml-px md:ml-0 md:w-1/4">
                  <div className="fs-0 [&_img]:h-auto [&_img]:w-full [&_img]:!relative [&>span>span]:!hidden [&>span]:!block">
                    <Image
                      src={recommendedBlogImage[index]?.sourceUrl ?? "/"}
                      width={312}
                      height={253}
                      alt={recommendedBlogImage[index]?.alt_text ?? ""}
                      className="rounded-lg overflow-hidden !w-full !h-auto"
                    />
                  </div>
                  <p className="text-sm text-primary-yellow pb-2 pt-7">
                    {recommendedBlogCategory[index]}
                  </p>
                  <h3 className="text-2xl font-bold text-primary-black pb-1 text-ellipsis overflow-hidden line-clamp-2">
                    {value.title.rendered}
                  </h3>
                  <p className="text-sm text-primary-black pb-3">
                    {formatDate(value.date)}
                  </p>
                  <div
                    className="text-sm text-primary-black text-ellipsis overflow-hidden line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: value.content.rendered }}
                  ></div>
                </a>
              </Link>
            ))}
          </div>
        </div>
      </Container>
    </Layout>
  );
}

export default BlogPost;

export async function getServerSideProps(context) {
  const { slug } = context.params;
  try {
    const response = await axios.get(
      `${cmsUrl}/wp-json/wp/v2/posts?slug=${slug}`
    );
    const postData = response.data[0];
    return {
      props: {
        postData,
      },
    };
  } catch (error) {
    console.error("Error fetching post data:", error);
    return {
      notFound: true,
    };
  }
}
